/**
 * Activity filter helpers (TRAV23 activities-front merge).
 *
 * The hotel equivalents in `./filters` take `IFiltersState` and build the hotel sort
 * list, so activities get their own versions rather than widened shared ones. Three
 * differences are deliberate:
 *
 *  - the sort list drops `MostBooked` (no quick-book for activities) and labels the B2C
 *    savings sort as "Recommended", matching the activities front;
 *  - the Capital Vacations variant hides the savings sorts entirely;
 *  - `EXCLUDED_ACTIVITY_FILTERS_KEYS` omits `propertyName`, which the hotel list
 *    excludes - the activity UI shows the name filter as a removable chip.
 */

import { FormattedMessage } from 'react-intl';
import { LabeledValue } from 'antd/lib/select';
import isEqual from 'lodash/isEqual';

import { SortTypes } from '@share/constants';
import {
  activityFiltersCapitalVacationsInitialState,
  activityFiltersInitialState,
  IActivityFiltersState,
} from '@share/store/slices';

export const getActivitySortOptions = (
  isB2C: boolean,
  walletWalletSavings: boolean,
  displayRecommended: boolean,
  displayRecommendedCustomerAgent: boolean,
  hasSavedProperties?: boolean,
  isCapitalVacations?: boolean,
): LabeledValue[] => {
  const sorts: LabeledValue[] = [];

  // Capital Vacations accounts never surface savings-based ordering.
  if (!isCapitalVacations) {
    if (displayRecommended) {
      sorts.push({ value: SortTypes.Recommended, label: <FormattedMessage id="sort.Recommended" /> });
    }
    if (displayRecommendedCustomerAgent) {
      sorts.push({
        value: SortTypes.RecommendedCustomerAgent,
        label: <FormattedMessage id="sort.Recommended" />,
      });
    }

    if (!isB2C || walletWalletSavings) {
      sorts.push({
        value: SortTypes.BiggestSavingsPercentage,
        label: <FormattedMessage id="sort.biggest.percent" />,
      });
      sorts.push({ value: SortTypes.BiggestSavings, label: <FormattedMessage id="sort.biggest" /> });
    } else {
      // B2C keeps the savings sort as the default but must not advertise savings.
      sorts.push({
        value: SortTypes.BiggestSavingsPercentage,
        label: <FormattedMessage id="sort.Recommended" />,
      });
    }
  }

  sorts.push(
    { value: SortTypes.RatingAsc, label: <FormattedMessage id="sort.low.to.rating" /> },
    { value: SortTypes.RatingDesc, label: <FormattedMessage id="sort.high.to.rating" /> },
    { value: SortTypes.PriceAsc, label: <FormattedMessage id="sort.lowest.price" /> },
    { value: SortTypes.PriceDesc, label: <FormattedMessage id="sort.highest.price" /> },
  );

  if (hasSavedProperties) {
    sorts.push({
      value: SortTypes.SavedProperties,
      label: <FormattedMessage id="activity.sort.saved.activities" />,
    });
  }

  return sorts;
};

export const isActivityFiltersChanged = (
  prevFilters: IActivityFiltersState,
  currentFilters: IActivityFiltersState,
): boolean => {
  return !isEqual(prevFilters, currentFilters);
};

export const isActivityFiltersEmpty = (
  currentFilters: IActivityFiltersState,
  isCapitalVacations?: boolean,
): boolean => {
  return isEqual(
    currentFilters,
    isCapitalVacations ? activityFiltersCapitalVacationsInitialState : activityFiltersInitialState,
  );
};

/**
 * Filter state that is not user-removable, so it never becomes a chip.
 *
 * `sortBy`, `distance` and `searchHomes` always hold a value; the autocomplete fields
 * are transient UI state.
 */
export const EXCLUDED_ACTIVITY_FILTERS_KEYS = [
  'autocompleteItems',
  'autocompleteLoading',
  'selectedAutocompleteItem',
  'selectedAutocompleteLabel',
  'searchHomes',
  'distance',
  'sortBy',
  'hideRatings',
];

export interface IActivityFilterValue {
  key: string;
  value: any;
  /** True when the value came from an array filter, so removal filters rather than clears. */
  isArray: boolean;
}

/** Flattens the active filters into one entry per removable chip. */
export const getActivityFilterValues = (
  filtersStore: IActivityFiltersState,
  extraExcludedKeys: string[] = [],
): IActivityFilterValue[] => {
  const excludedKeys = [...EXCLUDED_ACTIVITY_FILTERS_KEYS, ...extraExcludedKeys];

  return Object.keys(filtersStore)
    .filter((key) => !excludedKeys.includes(key))
    .filter((key) => {
      const value = filtersStore[key as keyof IActivityFiltersState];
      return (
        (Array.isArray(value) && value.length > 0) || (typeof value === 'string' && value !== '')
      );
    })
    .map((key) => {
      const value = filtersStore[key as keyof IActivityFiltersState];

      if (Array.isArray(value)) {
        return value.map((item: any) => ({ key, value: item, isArray: true }));
      }

      return [{ key, value, isArray: false }];
    })
    .flat();
};
